import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, MenuItem, Paper, Tab, Tabs, TextField, ThemeProvider, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faBuildingColumns, faCheck, faChevronDown, faCopy, faEyeSlash, faHandshake, faPlus, faWallet } from '@fortawesome/free-solid-svg-icons';
import { portalSecurityTheme } from '../components/McpUi';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { bonusApi, referralApi, referralErrorKey } from '../utils/referralApi';
import { CHANGED_EVENT, openLinkedInBonus } from '../components/LinkedInBonus';
import './sectionPages.css';
import './PartnerProgram.css';

// Partner program (/referral, ai-docs features/36): join with one click, share links, follow sign-ups and
// commissions. Referred customers are numbered, never named. The whole page appears at once (no layout shift).

// The sign-up page first: a partner link is there to win accounts (Robert, 29.10.2026).
const TARGETS = [
  { value: 'signup', key: 'partner_target_signup' },
  { value: '/', key: 'partner_target_home' },
  { value: '/pricing', key: 'partner_target_pricing' },
];

function useFormat() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const money = (minor, currency = 'usd') => {
    try { return new Intl.NumberFormat(lang, { style: 'currency', currency: currency.toUpperCase() }).format((minor || 0) / 100); }
    catch { return `${((minor || 0) / 100).toFixed(2)} ${currency.toUpperCase()}`; }
  };
  const date = (value) => value ? new Date(value).toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const count = (n) => new Intl.NumberFormat(lang).format(n || 0);
  const rate = (bps) => t('partner_rate', { rate: new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(bps / 100) });
  const duration = (terms) => terms.duration_months ? t('partner_offer_months', { months: terms.duration_months }) : t('partner_offer_forever');
  const target = (value) => { const known = TARGETS.find((x) => x.value === value); return known ? t(known.key) : value; };
  const termsUrl = `https://ultimo-bots.com${lang.startsWith('de') ? '/de' : ''}/referral/terms`;
  return { t, money, date, count, rate, duration, target, termsUrl };
}

function TermsLink({ label = 'partner_terms_read' }) {
  const { t, termsUrl } = useFormat();
  return <a className="partner-terms-link" href={termsUrl} target="_blank" rel="noopener noreferrer">{t(label)} <FontAwesomeIcon icon={faArrowUpRightFromSquare} /></a>;
}

function JoinView({ me, onJoined }) {
  const { t, money, rate, duration } = useFormat();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const offer = me.offer;
  async function join() {
    setBusy(true); setError('');
    try { await onJoined(await referralApi('join', { method: 'POST', body: { accept_terms: true, terms_version: me.terms_version } })); }
    catch (err) { setError(referralErrorKey(err)); setBusy(false); }
  }
  const rules = [
    t('partner_rule_link', { days: me.window_days }),
    offer.duration_months
      ? t('partner_rule_commission_months', { rate: rate(offer.rate_bps), months: offer.duration_months })
      : t('partner_rule_commission_forever', { rate: rate(offer.rate_bps) }),
    t('partner_rule_payout', { days: me.hold_days, amount: money(me.min_payout * 100) }),
    t('partner_rule_dashboard'),
  ];
  return <Paper variant="outlined" className="partner-card partner-join">
    <div className="partner-join-text">
      <span className="partner-icon-tile"><FontAwesomeIcon icon={faHandshake} /></span>
      <Typography component="h2" variant="h2">{t('partner_join_title')}</Typography>
      <ul className="partner-rules">{rules.map((rule) => <li key={rule}><FontAwesomeIcon icon={faCheck} /><span>{rule}</span></li>)}</ul>
    </div>
    <div className="partner-offer">
      <span className="partner-offer-label">{t('partner_offer_label')}</span>
      <span className="partner-offer-rate">{rate(offer.rate_bps)}</span>
      <span className="partner-offer-duration">{duration(offer)}</span>
      <Button variant="contained" size="large" disabled={busy} onClick={join}>{busy ? <CircularProgress size={18} color="inherit" /> : t('partner_join_button')}</Button>
      <Typography variant="caption" color="text.secondary">{t('partner_join_note')}</Typography>
      <TermsLink />
      {error && <Alert severity="error">{t(error)}</Alert>}
    </div>
  </Paper>;
}

function RenewTerms({ me, onJoined }) {
  const { t } = useFormat();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function accept() {
    setBusy(true); setError('');
    try { await onJoined(await referralApi('join', { method: 'POST', body: { accept_terms: true, terms_version: me.terms_version } })); }
    catch (err) { setError(referralErrorKey(err)); setBusy(false); }
  }
  return <Alert severity="info" className="partner-alert" action={<Button color="inherit" size="small" disabled={busy} onClick={accept}>{t('partner_renew_button')}</Button>}>
    {t('partner_renew_notice')} <TermsLink />{error && <div>{t(error)}</div>}
  </Alert>;
}

function Figures({ me }) {
  const { t, money } = useFormat();
  const f = me.figures;
  const balances = f.balances.length ? f.balances : [{ currency: 'usd', in_review: 0, payable: 0, paid_out: 0 }];
  const amounts = (field) => balances.map((b) => <span key={b.currency}>{money(b[field], b.currency)}</span>);
  const cells = [
    { label: t('partner_fig_clicks'), value: f.clicks_30d },
    { label: t('partner_fig_signups'), value: f.signups },
    { label: t('partner_fig_paying'), value: f.paying },
    { label: t('partner_fig_in_review'), value: amounts('in_review') },
    { label: t('partner_fig_payable'), value: amounts('payable') },
    { label: t('partner_fig_paid_out'), value: amounts('paid_out') },
  ];
  return <Paper variant="outlined" className="partner-card partner-figures">
    {cells.map((cell) => <div className="partner-figure" key={cell.label}>
      <span className="partner-figure-label">{cell.label}</span>
      <span className="partner-figure-value">{cell.value}</span>
    </div>)}
  </Paper>;
}

function CopyLink({ url }) {
  const { t } = useFormat();
  const [state, setState] = useState('');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  async function copy() {
    try { await navigator.clipboard.writeText(url); setState('partner_copied'); }
    catch { setState('partner_copy_failed'); }
    clearTimeout(timer.current); timer.current = setTimeout(() => setState(''), 3000);
  }
  return <div className="partner-copy">
    <code title={url}>{url.replace(/^https?:\/\//, '')}</code>
    <Tooltip title={t(state || 'partner_copy')} placement="top">
      <IconButton size="small" onClick={copy} aria-label={`${t('partner_copy')}: ${url}`}><FontAwesomeIcon icon={state === 'partner_copied' ? faCheck : faCopy} /></IconButton>
    </Tooltip>
    <span className="partner-sr-only" role="status">{state && t(state)}</span>
  </div>;
}

function NewLinkForm({ onDone, onCancel }) {
  const { t } = useFormat();
  const [name, setName] = useState('');
  const [target, setTarget] = useState('signup');
  const [path, setPath] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function create(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const data = await referralApi('links', { method: 'POST', body: { name: name.trim() || null, target: target === 'other' ? `/${path.trim().replace(/^\/+/, '')}` : target } });
      onDone(data.links);
    } catch (err) { setError(referralErrorKey(err)); setBusy(false); }
  }
  return <form className="partner-new-link" onSubmit={create}>
    <TextField size="small" label={t('partner_link_name')} placeholder={t('partner_link_name_placeholder')} value={name} onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 80 }} autoFocus />
    <TextField size="small" select label={t('partner_link_target')} value={target} onChange={(e) => setTarget(e.target.value)}>
      {TARGETS.map((x) => <MenuItem key={x.value} value={x.value}>{t(x.key)}</MenuItem>)}
      <MenuItem value="other">{t('partner_target_other')}</MenuItem>
    </TextField>
    {target === 'other' && <TextField size="small" label={t('partner_link_path')} placeholder="blog" value={path} onChange={(e) => setPath(e.target.value)} InputProps={{ startAdornment: <span className="partner-path-prefix">ultimo-bots.com/</span> }} />}
    <div className="partner-new-link-actions">
      <Button onClick={onCancel} disabled={busy}>{t('partner_cancel')}</Button>
      <Button type="submit" variant="contained" disabled={busy || (target === 'other' && !path.trim())}>{t('partner_link_create')}</Button>
    </div>
    {error && <Alert severity="error" className="partner-form-error">{t(error)}</Alert>}
  </form>;
}

function LinkRow({ link, active, onHide }) {
  const { t, target } = useFormat();
  return <div className={`partner-link-row${link.archived_at ? ' is-archived' : ''}`}>
    <div className="partner-link-title"><span>{link.name || t('partner_link_unnamed')}</span><span className="partner-link-target">{target(link.target)}</span></div>
    <CopyLink url={link.url} />
    <div className="partner-link-stats">
      <span><b>{link.clicks}</b>{t('partner_col_clicks')}</span>
      <span><b>{link.signups}</b>{t('partner_col_signups')}</span>
      <span><b>{link.paying}</b>{t('partner_col_paying')}</span>
    </div>
    {active && !link.archived_at
      ? <Tooltip title={t('partner_link_hide')}><IconButton size="small" className="partner-link-hide" aria-label={t('partner_link_hide')} onClick={() => onHide(link)}><FontAwesomeIcon icon={faEyeSlash} /></IconButton></Tooltip>
      : <span className="partner-link-hide" />}
  </div>;
}

function LinksCard({ links, active, onLinks }) {
  const { t } = useFormat();
  const [creating, setCreating] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [hiding, setHiding] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const visible = links.filter((l) => !l.archived_at);
  const hidden = links.filter((l) => l.archived_at);
  async function hide() {
    setBusy(true); setError('');
    try { onLinks((await referralApi(`links/${hiding.id}/archive`, { method: 'POST' })).links); setHiding(null); }
    catch (err) { setError(referralErrorKey(err)); }
    finally { setBusy(false); }
  }
  return <Paper variant="outlined" className="partner-card partner-links">
    <div className="partner-card-head">
      <Typography component="h2" variant="h2">{t('partner_links_title')}</Typography>
      {active && !creating && <Button size="small" startIcon={<FontAwesomeIcon icon={faPlus} />} onClick={() => setCreating(true)}>{t('partner_link_new')}</Button>}
    </div>
    {creating && <NewLinkForm onCancel={() => setCreating(false)} onDone={(next) => { onLinks(next); setCreating(false); }} />}
    <div className="partner-link-list">
      {visible.map((link) => <LinkRow key={link.id} link={link} active={active} onHide={setHiding} />)}
      {!visible.length && <Typography variant="body2" color="text.secondary">{t('partner_links_empty')}</Typography>}
      {showHidden && hidden.map((link) => <LinkRow key={link.id} link={link} active={false} />)}
    </div>
    {hidden.length > 0 && <Button size="small" className="partner-fold" onClick={() => setShowHidden((v) => !v)} endIcon={<FontAwesomeIcon icon={faChevronDown} className={showHidden ? 'is-open' : ''} />}>{t('partner_links_hidden', { count: hidden.length })}</Button>}
    <Dialog open={!!hiding} onClose={() => !busy && setHiding(null)} aria-labelledby="partner-hide-title" fullWidth maxWidth="xs">
      <DialogTitle id="partner-hide-title">{t('partner_link_hide_title')}</DialogTitle>
      <DialogContent><DialogContentText>{t('partner_link_hide_text')}</DialogContentText>{error && <Alert severity="error" sx={{ mt: 2 }}>{t(error)}</Alert>}</DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => { setHiding(null); setError(''); }}>{t('partner_cancel')}</Button><Button variant="contained" disabled={busy} onClick={hide}>{t('partner_link_hide')}</Button></DialogActions>
    </Dialog>
  </Paper>;
}

function PayoutDialog({ open, current, onClose, onSaved }) {
  const { t } = useFormat();
  const [method, setMethod] = useState(current?.method || 'bank');
  const [holder, setHolder] = useState('');
  const [iban, setIban] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function save(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const body = method === 'bank' ? { method, holder, iban } : { method, email };
      onSaved((await referralApi('payout-details', { method: 'PUT', body })).payout);
    } catch (err) { setError(referralErrorKey(err)); }
    finally { setBusy(false); }
  }
  const ready = method === 'bank' ? holder.trim() && iban.trim() : email.trim();
  return <Dialog open={open} onClose={() => !busy && onClose()} aria-labelledby="partner-payout-title" fullWidth maxWidth="xs">
    <form onSubmit={save}>
      <DialogTitle id="partner-payout-title">{t('partner_payout_title')}</DialogTitle>
      <DialogContent className="partner-payout-form">
        <ToggleButtonGroup exclusive fullWidth size="small" value={method} onChange={(_, value) => value && setMethod(value)} aria-label={t('partner_payout_method')}>
          <ToggleButton value="bank">{t('partner_payout_bank')}</ToggleButton>
          <ToggleButton value="paypal">PayPal</ToggleButton>
        </ToggleButtonGroup>
        {method === 'bank' ? <>
          <TextField size="small" label={t('partner_payout_holder')} value={holder} onChange={(e) => setHolder(e.target.value)} autoComplete="name" />
          <TextField size="small" label="IBAN" value={iban} onChange={(e) => setIban(e.target.value)} placeholder="CH93 0076 2011 6238 5295 7" inputProps={{ spellCheck: false }} />
        </> : <TextField size="small" type="email" label={t('partner_payout_email')} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />}
        <Typography variant="caption" color="text.secondary">{t('partner_payout_privacy')}</Typography>
        {error && <Alert severity="error">{t(error)}</Alert>}
      </DialogContent>
      <DialogActions><Button disabled={busy} onClick={onClose}>{t('partner_cancel')}</Button><Button type="submit" variant="contained" disabled={busy || !ready}>{t('partner_save')}</Button></DialogActions>
    </form>
  </Dialog>;
}

// The partner's deal in one slim bar under the figures (Robert, 29.10.2026): rate and duration, the rules, payout details.
function TermsBar({ me, active, onPayout }) {
  const { t, money, rate, duration } = useFormat();
  const [editing, setEditing] = useState(false);
  const payout = me.payout;
  const cell = (label, value, className = '') => <div className={`partner-term ${className}`.trim()}>
    <span className="partner-term-label">{label}</span><span className="partner-term-value">{value}</span>
  </div>;
  return <Paper variant="outlined" className="partner-card partner-termsbar">
    {cell(t('partner_terms_title'), <><b className="partner-rate-value">{rate(me.terms.rate_bps)}</b> {duration(me.terms)}</>, 'is-main')}
    {cell(t('partner_fact_hold_label'), t('partner_fact_hold', { days: me.hold_days }))}
    {cell(t('partner_fact_payout_label'), t('partner_fact_payout', { amount: money(me.min_payout * 100) }))}
    {cell(t('partner_fact_tax_label'), t('partner_fact_tax'))}
    <div className="partner-term is-payout">
      <span className="partner-term-label">{t('partner_payout_title')}</span>
      <span className="partner-term-value">
        {payout
          ? <span className="partner-payout-line"><FontAwesomeIcon icon={payout.method === 'bank' ? faBuildingColumns : faWallet} />{payout.method === 'bank' ? payout.iban : `PayPal · ${payout.email}`}</span>
          : <span className="partner-payout-line is-empty">{t('partner_payout_none')}</span>}
        {active && <Button size="small" onClick={() => setEditing(true)}>{t(payout ? 'partner_payout_change' : 'partner_payout_add')}</Button>}
      </span>
    </div>
    <div className="partner-term is-link"><TermsLink label="partner_terms_short" /></div>
    {editing && <PayoutDialog open current={payout} onClose={() => setEditing(false)} onSaved={(next) => { onPayout(next); setEditing(false); }} />}
  </Paper>;
}

function StateLabel({ row }) {
  const { t, date } = useFormat();
  const reversal = row.kind === 'reversal';
  if (row.state === 'void') return <Chip size="small" label={t('partner_state_void')} />;
  if (row.state === 'paid') return <span className="partner-state is-paid">{t(reversal ? 'partner_state_deducted_on' : 'partner_state_paid', { date: date(row.paid_out_at) })}</span>;
  if (row.state === 'in_review') return <span className="partner-state">{t('partner_state_in_review', { date: date(row.payable_from) })}</span>;
  return <span className={`partner-state ${reversal ? 'is-deducted' : 'is-payable'}`}>{t(reversal ? 'partner_state_deducted' : 'partner_state_payable')}</span>;
}

function ActivityCard({ activity, terms }) {
  const { t, money, date } = useFormat();
  const [tab, setTab] = useState('customers');
  const { customers, commissions } = activity;
  const total = (list) => list.length ? list.map((x) => money(x.amount_minor, x.currency)).join(' · ') : '—';
  const until = (c) => {
    if (c.status === 'void') return <Chip size="small" label={t('partner_state_void')} />;
    if (!terms.duration_months) return t('partner_until_forever');
    return c.runs_until ? date(c.runs_until) : t('partner_until_first_payment', { months: terms.duration_months });
  };
  return <Paper variant="outlined" className="partner-card partner-activity">
    <Tabs value={tab} onChange={(_, value) => setTab(value)} className="partner-tabs">
      <Tab value="customers" label={`${t('partner_tab_customers')} (${customers.length})`} />
      <Tab value="commissions" label={`${t('partner_tab_commissions')} (${commissions.length})`} />
    </Tabs>
    <div className={`partner-table-wrap${(tab === 'customers' ? customers : commissions).length ? ' has-rows' : ''}`} role="tabpanel">
      {tab === 'customers' && (customers.length ? <table className="partner-table">
        <thead><tr><th>{t('partner_col_customer')}</th><th>{t('partner_col_signed_up')}</th><th>{t('partner_col_link')}</th><th>{t('partner_col_paying_since')}</th><th className="is-num">{t('partner_col_commission_total')}</th><th>{t('partner_col_until')}</th></tr></thead>
        <tbody>{customers.map((c) => <tr key={c.number}>
          <td>{t('partner_customer_number', { number: c.number })}</td><td>{date(c.signed_up)}</td><td>{c.link_name || t('partner_link_unnamed')}</td>
          <td>{c.paying_since ? date(c.paying_since) : t('partner_not_paying')}</td><td className="is-num">{total(c.commission)}</td><td>{until(c)}</td>
        </tr>)}</tbody>
      </table> : <p className="partner-empty">{t('partner_customers_empty')}</p>)}
      {tab === 'commissions' && (commissions.length ? <table className="partner-table">
        <thead><tr><th>{t('partner_col_date')}</th><th>{t('partner_col_customer')}</th><th className="is-num">{t('partner_col_payment')}</th><th className="is-num">{t('partner_col_commission')}</th><th>{t('partner_col_status')}</th></tr></thead>
        <tbody>{commissions.map((m) => <tr key={m.id}>
          <td>{date(m.date)}</td><td>{m.customer_number ? t('partner_customer_number', { number: m.customer_number }) : '—'}</td>
          <td className="is-num">{m.kind === 'reversal' ? t('partner_refund') : money(m.basis_minor, m.currency)}</td>
          <td className="is-num">{money(m.amount_minor, m.currency)}</td><td><StateLabel row={m} /></td>
        </tr>)}</tbody>
      </table> : <p className="partner-empty">{t('partner_commissions_empty')}</p>)}
    </div>
  </Paper>;
}

// The LinkedIn bonus in one slim line (ai-docs features/37): the offer, or where the user's post stands.
function BonusBar({ bonus }) {
  const { t, count } = useFormat();
  if (!bonus?.eligible) return null;
  const c = bonus.claim;
  const credits = count(bonus.credits);
  let text, action = null, tone = '';
  if (!c) { text = t('bonus_bar_offer', { credits }); action = t('bonus_bar_cta'); }
  else if (c.status === 'submitted') { text = t('bonus_bar_submitted'); tone = ' is-wait'; }
  else if (c.status === 'approved') { text = t('bonus_bar_approved', { credits, bot: c.bot_name || '' }); tone = ' is-ok'; }
  else { text = t('bonus_bar_rejected', { reason: c.reject_reason }); tone = ' is-warn'; if (c.resubmit_allowed) action = t('bonus_bar_resubmit'); }
  return <div className={`partner-bonus${tone}`}>
    <span className="partner-bonus-icon"><FontAwesomeIcon icon={faLinkedin} /></span>
    <span className="partner-bonus-text">{text}</span>
    {action && <Button size="small" variant="contained" onClick={() => openLinkedInBonus('partner_page')}>{action}</Button>}
  </div>;
}

export default function PartnerProgram() {
  const { t } = useTranslation();
  const [me, setMe] = useState(null);
  const [activity, setActivity] = useState({ customers: [], commissions: [] });
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [bonus, setBonus] = useState(null);

  // The bonus modal reports a submitted post, so the line updates without a reload.
  useEffect(() => {
    const onChange = (e) => setBonus(e.detail);
    window.addEventListener(CHANGED_EVENT, onChange);
    return () => window.removeEventListener(CHANGED_EVENT, onChange);
  }, []);

  // Customers and commissions only exist for an active partner; loaded with /me so the page renders once.
  const loadActivity = useCallback(async (data) => {
    if (!data.joined || data.status !== 'active') return { customers: [], commissions: [] };
    const [c, m] = await Promise.all([referralApi('customers'), referralApi('commissions')]);
    return { customers: c.customers, commissions: m.commissions };
  }, []);

  useEffect(() => {
    let live = true;
    setError('');
    (async () => {
      try {
        const [data, bonusState] = await Promise.all([referralApi('me'), bonusApi('linkedin/state').catch(() => null)]);
        const extra = await loadActivity(data);
        if (live) { setActivity(extra); setBonus(bonusState); setMe(data); }
      } catch (err) { if (live) setError(referralErrorKey(err)); }
    })();
    return () => { live = false; };
  }, [retry, loadActivity]);

  async function joined(data) {
    setActivity(await loadActivity(data));
    setMe(data);
  }

  const active = me?.status === 'active';
  return <ThemeProvider theme={portalSecurityTheme}><div className={`partner-page${me?.joined && active ? ' is-dashboard' : ''}`}>
    <header className="section-head">
      <h1 className="section-head-title">{t('partner_title')}</h1>
      <p className="section-head-sub">{t(me?.joined ? 'partner_subtitle_joined' : 'partner_subtitle')}</p>
    </header>
    {error && <Alert severity="error" className="partner-alert" action={<Button color="inherit" size="small" onClick={() => setRetry((v) => v + 1)}>{t('partner_retry')}</Button>}>{t(error)}</Alert>}
    {!me && !error && <div className="partner-loading" role="status"><CircularProgress size={22} /></div>}
    {me && !me.joined && <div className="partner-in"><BonusBar bonus={bonus} /><JoinView me={me} onJoined={joined} /></div>}
    {me?.joined && <div className="partner-in">
      {!active && <Alert severity="warning" className="partner-alert">{t('partner_blocked_notice')}</Alert>}
      {active && me.needs_terms && <RenewTerms me={me} onJoined={joined} />}
      <Figures me={me} />
      <TermsBar me={me} active={active} onPayout={(payout) => setMe((m) => ({ ...m, payout }))} />
      {active && <BonusBar bonus={bonus} />}
      <LinksCard links={me.links} active={active} onLinks={(links) => setMe((m) => ({ ...m, links }))} />
      {active && <ActivityCard activity={activity} terms={me.terms} />}
    </div>}
  </div></ThemeProvider>;
}
